
import { useCallback, useRef } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { createChatCompletion } from '@/services/openai-secure';
import { TranscriptionConfidence } from './types';

export const useOpenAIProcessing = () => {
  const { toast } = useToast();
  const isTranscribingRef = useRef(false);

  const processWithOpenAI = useCallback(async (audioText: string): Promise<{ text: string; confidence: TranscriptionConfidence }> => {
    try {
      const response = await createChatCompletion([
        {
          role: 'system',
          content: 'You are a text processing assistant. Clean up and improve the following transcribed text, maintaining the original meaning. Only return the improved text, no additional commentary.'
        },
        {
          role: 'user',
          content: `Please clean up this transcribed text: "${audioText}"`
        }
      ]);

      if (response.error) {
        throw new Error(response.error);
      }

      return {
        text: response.content,
        confidence: {
          text: response.content,
          overallConfidence: 0.9,
          words: [],
          needsReprocessing: false,
          isHighQuality: true
        }
      };
    } catch (error) {
      console.error('OpenAI fallback failed:', error);
      return {
        text: audioText,
        confidence: {
          text: audioText,
          overallConfidence: 0.5,
          words: [],
          needsReprocessing: true,
          isHighQuality: false
        }
      };
    }
  }, []);

  const retranscribe = useCallback(async (text: string, onResult: (text: string, confidence: TranscriptionConfidence) => void) => {
    if (!text || isTranscribingRef.current) return;

    isTranscribingRef.current = true;
    try {
      const result = await processWithOpenAI(text);
      onResult(result.text, result.confidence);
      return result;
    } catch (e) {
      toast({
        title: "Erro ao reprocessar",
        description: "Não foi possível melhorar a transcrição.",
        variant: "destructive"
      });
      throw e;
    } finally {
      isTranscribingRef.current = false;
    }
  }, [processWithOpenAI, toast]);

  return {
    processWithOpenAI,
    retranscribe,
    isTranscribingRef
  };
};
